import type { HubConnection } from '@microsoft/signalr';
import { HubConnectionState } from '@microsoft/signalr';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSignalRContext } from 'react-signalr';
import type { Hub } from 'react-signalr/lib/signalr/types';

import UnstableConnectionIcon from '../../components/shared/icon/UnstableConnectionIcon';
import { useBanner } from '../BannerContext';

type ConfigType = {
  url: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dependencies: any[];
  disable?: boolean;
  /**
   * Forwarded to the SignalR HTTP connection. Leave undefined for cookie-authed hubs on the
   * core API (default sends credentials); set `false` for cross-origin services that use a
   * non-cookie auth (e.g. the forms V2 hub with its DevUser bypass) so the negotiate/WS
   * request isn't blocked by CORS (`Allow-Origin: *` is incompatible with credentials).
   */
  withCredentials?: boolean;
};

const maxConnectionErrors = 10;
const maxConnectionErrorsToRead = 2;
const recentMilliseconds = 60 * 1000; // 1 minute

export const useSignalRHub = <THub extends Hub<string, string>>(props: ConfigType) => {
  const { url, dependencies, disable, withCredentials } = props;
  const banners = useBanner();
  const { t } = useTranslation('common');

  // False by default, will set to `!disabled` once mounted
  const [shouldConnect, setShouldConnect] = useState(false);
  // The live SignalR connection's id; null while connecting, reconnecting or closed.
  // A reconnect yields a new id, so effects keyed on it re-run (e.g. rejoining hub groups).
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [connectionErrorTimes, setConnectionErrorTimes] = useState<number[]>([]);
  const [errorBannerId, setErrorBannerId] = useState<string | null>(null);

  const addConnectionError = useCallback(() => {
    setConnectionErrorTimes((prev) => [...prev, Date.now()].slice(-maxConnectionErrors));
  }, []);

  useEffect(() => {
    // Don't show a toaster for "once off errors"
    if (connectionErrorTimes.length < maxConnectionErrorsToRead) return;

    const allWereRecent = connectionErrorTimes.slice(0, maxConnectionErrorsToRead).every((time) => Date.now() - time < recentMilliseconds);

    if (allWereRecent && !errorBannerId) {
      const id = banners.showBanner({
        icon: <UnstableConnectionIcon className="h-5 w-5" />,
        message: t('bad-connection.unstable'),
      });

      setErrorBannerId(id);
    }
  }, [banners, connectionErrorTimes, errorBannerId, t]);

  useEffect(() => {
    setShouldConnect(!disable);
  }, [disable]);

  const context = useMemo(
    () =>
      createSignalRContext<THub>({
        shareConnectionBetweenTab: false,
      }),
    [],
  );

  const onOpen = useCallback((connection: HubConnection) => {
    setConnectionId(connection.state === HubConnectionState.Connected ? (connection.connectionId ?? null) : null);
  }, []);

  const onClosed = useCallback(() => {
    setConnectionId(null);
  }, []);

  const onError = useCallback(() => {
    addConnectionError();
    // react-signalr routes `onreconnecting` here; drop the id so the next one counts as new.
    if (context.connection?.state !== HubConnectionState.Connected) setConnectionId(null);

    return Promise.resolve();
  }, [addConnectionError, context]);

  const onReconnect = useCallback(
    (connection: HubConnection) => {
      onOpen(connection);

      if (errorBannerId) {
        setErrorBannerId(null);
        banners.removeBanner(errorBannerId);
      }
    },
    [banners, errorBannerId, onOpen],
  );

  // eslint-disable-next-line react-hooks/use-memo, react-hooks/exhaustive-deps
  const deps = useMemo(() => dependencies, [...dependencies]);

  const onOpenRef = useRef(onOpen);
  const onClosedRef = useRef(onClosed);
  const onErrorRef = useRef(onError);
  const onReconnectRef = useRef(onReconnect);

  useEffect(() => {
    onOpenRef.current = onOpen;
    onClosedRef.current = onClosed;
    onErrorRef.current = onError;
    onReconnectRef.current = onReconnect;
  }, [onClosed, onError, onOpen, onReconnect]);

  const provider = useMemo(() => {
    return (
      <context.Provider
        url={url}
        dependencies={deps}
        withCredentials={withCredentials}
        onOpen={(x) => onOpenRef.current?.(x)}
        onBeforeClose={() => onClosedRef.current?.()}
        onClosed={() => onClosedRef.current?.()}
        onError={() => onErrorRef.current?.()}
        onReconnect={(x) => onReconnectRef.current?.(x)}
        connectEnabled={shouldConnect}
      >
        {null /* children are not used in this context, but required by the component */}
      </context.Provider>
    );
  }, [context, url, deps, shouldConnect, withCredentials]);

  return {
    context,
    invoke: context.invoke,
    useSignalREffect: context.useSignalREffect,
    isConnected: !!connectionId,
    connectionId,
    signalRConnection: provider,
  };
};
